import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Gift, PublicWishlistPayload, Reservation } from "@wishlist-hub/shared";
import { Button, Card, ErrorText, Input, Label } from "../components/ui";
import { api } from "../services/api";

const reserveSchema = z.object({
  guestName: z.string().min(1).max(100),
  guestEmail: z.string().email().optional().or(z.literal("")),
  message: z.string().max(500).optional(),
  quantity: z.coerce.number().int().positive(),
});

type ReserveForm = z.infer<typeof reserveSchema>;

export function WishlistView() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<PublicWishlistPayload | null>(null);
  const [error, setError] = useState("");
  const [giftToReserve, setGiftToReserve] = useState<Gift | null>(null);
  const [done, setDone] = useState<Reservation | null>(null);

  const load = () => {
    if (!slug) return;
    api
      .get<PublicWishlistPayload>(`/wishlists/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setError("Список не найден"));
  };

  useEffect(() => {
    load();
  }, [slug]);

  const form = useForm<ReserveForm>({
    resolver: zodResolver(reserveSchema),
    defaultValues: { quantity: 1, guestName: "", guestEmail: "", message: "" },
  });

  const submitReserve = form.handleSubmit(async (values) => {
    if (!slug || !giftToReserve) return;
    setError("");
    try {
      const { data: res } = await api.post<Reservation>(`/wishlists/${slug}/reserve`, {
        giftId: giftToReserve.id,
        guestName: values.guestName,
        guestEmail: values.guestEmail || undefined,
        message: values.message || undefined,
        quantity: values.quantity,
      });
      setDone(res);
      setGiftToReserve(null);
      form.reset({ ...form.getValues(), guestName: "", message: "" });
      load();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "response" in e
          ? String((e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message)
          : "";
      setError(msg || "Не удалось забронировать");
    }
  });

  if (error && !data) {
    return <p className="text-red-400">{error}</p>;
  }
  if (!data) {
    return <p className="text-slate-400">Загрузка…</p>;
  }

  const { wishlist, gifts, manage } = data;

  const adminHeaders = manage
    ? { headers: { "X-Admin-Token": manage.adminToken } }
    : undefined;

  const deleteWishlist = async () => {
    if (!manage || !confirm("Удалить весь список и все подарки? Это необратимо.")) return;
    try {
      await api.delete(`/admin/wishlists/${manage.adminToken}`);
      nav("/");
    } catch {
      setError("Не удалось удалить список");
    }
  };

  const deleteGift = async (g: Gift) => {
    if (!manage || !confirm(`Удалить подарок «${g.title}»?`)) return;
    try {
      await api.delete(`/admin/gifts/${g.id}`, adminHeaders);
      load();
    } catch {
      setError("Не удалось удалить подарок");
    }
  };

  return (
    <>
      <Helmet>
        <title>{wishlist.title} — WishList Hub</title>
      </Helmet>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white">{wishlist.title}</h1>
          {wishlist.description && (
            <p className="mt-2 text-slate-400 whitespace-pre-wrap">{wishlist.description}</p>
          )}
          {manage && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-violet-500/30 bg-violet-950/20 px-4 py-3">
              <span className="text-sm text-slate-300">Вы владелец списка</span>
              <Button
                type="button"
                variant="secondary"
                className="!py-1.5 !px-3"
                onClick={() => nav(`/admin/${manage.adminToken}`)}
              >
                Редактировать список
              </Button>
              <Button type="button" variant="danger" className="!py-1.5 !px-3" onClick={deleteWishlist}>
                Удалить список
              </Button>
            </div>
          )}
        </div>
        {gifts.length === 0 ? (
          <p className="text-slate-500">Пока нет подарков или все скрыты в режиме сюрприза.</p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {gifts.map((g) => (
              <li key={g.id}>
                <Card className="h-full flex flex-col">
                  <div className="flex-1">
                    {g.imageUrl && (
                      <img
                        src={g.imageUrl}
                        alt=""
                        className="mb-3 h-32 w-full rounded-lg object-cover"
                      />
                    )}
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      {g.title}
                      {g.priority === 1 && (
                        <span className="text-xs font-normal text-amber-400">★ приоритет</span>
                      )}
                    </h3>
                    {g.description && (
                      <p className="mt-1 text-sm text-slate-400">{g.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                      {g.price != null && <span>{g.price} ₽</span>}
                      {g.url && (
                        <a href={g.url} className="text-violet-400 hover:underline" target="_blank" rel="noreferrer">
                          Ссылка
                        </a>
                      )}
                      {!wishlist.isPrivate && (
                        <span>
                          Осталось: {g.quantity - (g.reservedCount ?? 0)} / {g.quantity}
                        </span>
                      )}
                    </div>
                  </div>
                  {manage && (
                    <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-800 pt-3 text-xs">
                      <Link
                        to={`/admin/${manage.adminToken}#gift-${g.id}`}
                        className="font-medium text-violet-400 hover:text-violet-300 hover:underline"
                      >
                        Изменить подарок
                      </Link>
                      <button
                        type="button"
                        className="font-medium text-red-400 hover:text-red-300 hover:underline"
                        onClick={() => deleteGift(g)}
                      >
                        Удалить подарок
                      </button>
                    </div>
                  )}
                  <Button
                    className="mt-4 w-full"
                    disabled={(g.reservedCount ?? 0) >= g.quantity}
                    onClick={() => {
                      setDone(null);
                      setGiftToReserve(g);
                      form.setValue("quantity", 1);
                    }}
                  >
                    Забронировать
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {giftToReserve && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <Card className="max-w-md w-full">
            <h3 className="text-lg font-semibold text-white">Бронь: {giftToReserve.title}</h3>
            <form className="mt-4 space-y-3" onSubmit={submitReserve}>
              <div>
                <Label>Ваше имя</Label>
                <Input {...form.register("guestName")} />
                {form.formState.errors.guestName && (
                  <ErrorText>{form.formState.errors.guestName.message}</ErrorText>
                )}
              </div>
              <div>
                <Label>Email (для отмены брони)</Label>
                <Input type="email" {...form.register("guestEmail")} />
              </div>
              <div>
                <Label>Количество</Label>
                <Input type="number" min={1} {...form.register("quantity")} />
              </div>
              <div>
                <Label>Сообщение</Label>
                <Input {...form.register("message")} />
              </div>
              {error && <ErrorText>{error}</ErrorText>}
              <div className="flex gap-2">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  Подтвердить
                </Button>
                <Button type="button" variant="secondary" onClick={() => setGiftToReserve(null)}>
                  Отмена
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {done && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <Card className="max-w-md w-full">
            <h3 className="text-lg font-semibold text-white">Забронировано</h3>
            <p className="mt-2 text-sm text-slate-400">
              Сохраните ссылку для отмены (никому не отправляйте, если хотите сохранить сюрприз):
            </p>
            <code className="mt-2 block break-all text-xs text-violet-300">
              {`${window.location.origin}/cancel-reservation/${done.reservationToken}`}
            </code>
            <p className="mt-2 text-xs text-slate-500">
              Или отмените через DELETE /api/reservations/:token
            </p>
            <Button className="mt-4" onClick={() => setDone(null)}>
              Закрыть
            </Button>
          </Card>
        </div>
      )}
    </>
  );
}
